import { useCallback, useEffect, useRef } from 'react';

import { ActionKey, ActionSubjectIdKey, ActionSubjectKey } from '@/analytics/constants';
import { useFastshiftAnalyticsData } from '@/analytics/hooks/use-fastshift-analytics-data';
import { useFireAnalyticsEvents } from '@/analytics/use-fire-analytics-events';

import type { Organization } from '../types';

type OrganizationsFlyoutAnalyticsProps = {
	isOpen: boolean;
	organizations: Organization[] | null;
	error: string | null;
	pending: boolean;
};

type DisplayAttributes =
	| { status: 'normal' | 'empty'; organizationCount: number }
	| { status: 'error' };

export const useOrganizationsFlyoutAnalytics = ({
	isOpen,
	organizations,
	error,
	pending,
}: OrganizationsFlyoutAnalyticsProps) => {
	const {
		sourceKey,
		identityAttrs: displayIdentityAttrs,
		uiIdentityAttrs,
	} = useFastshiftAnalyticsData();
	const { fireUIEvent } = useFireAnalyticsEvents(uiIdentityAttrs);
	const { fireTrackEvent } = useFireAnalyticsEvents(displayIdentityAttrs);
	const hasFiredDisplay = useRef(false);

	useEffect(() => {
		if (!isOpen) {
			hasFiredDisplay.current = false;
			return;
		}
		if (!displayIdentityAttrs || hasFiredDisplay.current) return;

		const hasOrganizations = Boolean(organizations?.length);

		if (!hasOrganizations && pending) return;
		if (organizations === null && !error) return;

		let attributes: DisplayAttributes;
		if (organizations?.length) {
			attributes = { status: 'normal', organizationCount: organizations.length };
		} else if (error) {
			attributes = { status: 'error' };
		} else {
			attributes = { status: 'empty', organizationCount: 0 };
		}

		hasFiredDisplay.current = true;
		fireTrackEvent({
			action: ActionKey.DISPLAYED,
			actionSubject: ActionSubjectKey.SECTION,
			actionSubjectId: ActionSubjectIdKey.ORGANIZATIONS_FLYOUT,
			source: sourceKey,
			attributes,
		});
	}, [isOpen, organizations, error, pending, sourceKey, displayIdentityAttrs, fireTrackEvent]);

	const sendSideNavFlyoutButtonClick = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.BUTTON,
			actionSubjectId: ActionSubjectIdKey.SIDE_NAV_FLYOUT_BUTTON,
			source: sourceKey,
			attributes: { title: 'Organizations', isDomainScopedView: true },
		});
	}, [fireUIEvent, sourceKey]);

	const sendOrganizationLinkClick = useCallback(
		(selectedId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.LINK,
				actionSubjectId: ActionSubjectIdKey.FLYOUT_ITEM_LINK,
				source: sourceKey,
				attributes: { type: 'organization', selectedId },
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendViewAllLinkClick = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.LINK,
			actionSubjectId: ActionSubjectIdKey.FLYOUT_VIEW_ALL_LINK,
			source: sourceKey,
			attributes: { type: 'organization' },
		});
	}, [fireUIEvent, sourceKey]);

	return { sendSideNavFlyoutButtonClick, sendOrganizationLinkClick, sendViewAllLinkClick };
};
